package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.ErrorState
import co.maytes.standupbot.dashboard.components.feedback.ConfirmModal
import co.maytes.standupbot.dashboard.components.feedback.ConfirmTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.SegmentedControl
import co.maytes.standupbot.dashboard.components.widgets.SegmentedOption
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.standupruns.tabs.AiCostTab
import co.maytes.standupbot.dashboard.features.standupruns.tabs.RawActivityTab
import co.maytes.standupbot.dashboard.features.standupruns.tabs.SectionsTab
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.UiContainer
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.model.ModelDto
import co.maytes.standupbot.shared.dto.standuprun.RunCauseDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunDetailDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunRevisionDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunStatusDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.css.px

@Composable
@Suppress("LongParameterList", "LongMethod")
fun StandupDetailView(
    detail: StandupRunDetailDto,
    isAdmin: Boolean,
    onRerun: () -> Unit,
    rerunInFlight: Boolean,
    currentUserId: String = "",
    workspaceId: String = "",
    standupId: String = "",
    store: StandupRunsStore? = null,
    ui: UiContainer? = null,
    onRegenerate: ((model: String) -> Unit)? = null,
    regenerateInFlight: Boolean = false,
    regenerateError: String? = null,
    onDismissRegenerateError: () -> Unit = {},
    onPostDraftNow: () -> Unit = {},
    onDiscardDraft: () -> Unit = {},
    draftActionInFlight: Boolean = false,
    rawActivityRetentionDays: Int = 30,
) {
    var active by remember { mutableStateOf(DetailTab.Sections) }
    val tabs = buildList {
        add(DetailTab.Sections)
        add(DetailTab.RawActivity)
        add(DetailTab.AiCost)
        if (isAdmin) add(DetailTab.Debug)
    }

    var editOpen by remember { mutableStateOf(false) }
    var regenerateOpen by remember { mutableStateOf(false) }
    var discardOpen by remember { mutableStateOf(false) }
    var sectionsView by remember { mutableStateOf(SectionsView.SlackPreview) }
    var selectedRevisionId by remember(detail.id) {
        mutableStateOf(detail.currentRevision?.id)
    }
    val scope = rememberCoroutineScope()
    val currentSection = detail.sections.firstOrNull { it.userId == currentUserId }

    var models by remember { mutableStateOf(AVAILABLE_MODELS) }
    LaunchedEffect(store) {
        val res = store?.fetchModels()
        models = if (res is Result.Ok && res.value.isNotEmpty()) res.value else AVAILABLE_MODELS
    }

    val selectedRevision =
        detail.revisions.firstOrNull { it.id == selectedRevisionId } ?: detail.currentRevision

    Column(modifier = Modifier.fillMaxWidth()) {
        if (regenerateError != null) {
            Box(modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.md)) {
                ErrorState(
                    title = "Regenerate failed",
                    message = regenerateError,
                    retryLabel = "Dismiss",
                    onRetry = onDismissRegenerateError,
                )
            }
        }
        DetailHeader(
            detail = detail,
            isAdmin = isAdmin,
            rerunInFlight = rerunInFlight,
            onRerun = onRerun,
            onRegenerate =
                if (isAdmin && onRegenerate != null && RunStatusRules.canRegenerate(detail.status)) {
                    { regenerateOpen = true }
                } else {
                    null
                },
            regenerateInFlight = regenerateInFlight,
        )

        if (detail.revisions.size > 1) {
            Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md)) {
                RevisionsPillRow(
                    revisions = detail.revisions,
                    selectedId = selectedRevisionId ?: detail.currentRevision?.id,
                    onSelect = { selectedRevisionId = it },
                )
            }
        }

        if (regenerateOpen && onRegenerate != null) {
            RegenerateStandupModal(
                current = detail,
                models = models,
                submitting = regenerateInFlight,
                onDismiss = { regenerateOpen = false },
                onConfirm = { model ->
                    onRegenerate(model)
                    regenerateOpen = false
                },
            )
        }

        if (detail.status == StandupRunStatusDto.FAILED) {
            Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md)) {
                FailureBanner(
                    errorCode = detail.errorCode,
                    errorMessage = detail.errorMessage,
                    cause = detail.cause,
                )
            }
        }

        if (detail.status != StandupRunStatusDto.FAILED && RunCauseRules.showsIncompleteNotice(detail.warnings, detail.cause)) {
            Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md)) {
                IncompleteRunNotice(detail.warnings, detail.cause)
            }
        }

        if (RunStatusRules.isWorking(detail.status)) {
            Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md)) {
                GeneratingBanner(detail.status)
            }
        }

        if (detail.status == StandupRunStatusDto.DRAFTED) {
            Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md)) {
                DraftBanner(
                    postsAt = detail.scheduledFor,
                    isAdmin = isAdmin,
                    inFlight = draftActionInFlight,
                    onPostNow = onPostDraftNow,
                    onDiscard = { discardOpen = true },
                )
            }
        }

        if (discardOpen) {
            ConfirmModal(
                title = RunStatusRules.DISCARD_TITLE,
                body = RunStatusRules.DISCARD_BODY,
                confirmLabel = "Discard draft",
                tone = ConfirmTone.Danger,
                onConfirm = {
                    discardOpen = false
                    onDiscardDraft()
                },
                onDismiss = { discardOpen = false },
            )
        }

        if (isAdmin) {
            Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md)) {
                RunDraftsSection(store = store, workspaceId = workspaceId, runId = detail.id, status = detail.status)
            }
        }

        val fullBanner = RunStatusRules.showsNoActivityBanner(detail.status, detail.sections.size)
        if (fullBanner || QuietRunRules.needsAttention(detail.sections)) {
            Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md)) {
                NoActivityBanner(detail, compact = !fullBanner)
            }
        }

        val quietRows = QuietRunRules.rows(detail.sections)
        if (detail.leave.isNotEmpty() || quietRows.isNotEmpty()) {
            val pageContext = rememberPageContext()
            Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md)) {
                ExcludedPanel(
                    leave = detail.leave,
                    postDate = detail.scheduledFor.toLocalDateTime(TimeZone.currentSystemDefault()).date,
                    quiet = quietRows,
                    isAdmin = isAdmin,
                    canRerun = RunStatusRules.canRerun(detail.status) && !rerunInFlight,
                    onOpenRoster = { detail.standupId?.let { pageContext.router.navigateTo("/app/standups/$it") } },
                    onRerun = onRerun,
                )
            }
        }

        AtRiskRules.incompleteMessage(detail.warnings).let { incomplete ->
            if (detail.atRisk.isNotEmpty() || incomplete != null) {
                Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md)) {
                    AtRiskPanel(detail.atRisk, incomplete, TimeZone.currentSystemDefault())
                }
            }
        }

        Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md, bottom = Spacing.md)) {
            StandupDetailTabsBar(
                visibleTabs = tabs,
                active = active,
                onSelect = { active = it },
            )
        }

        if (editOpen && currentSection != null) {
            SectionEditModal(
                currentSection = currentSection,
                layout = detail.templateSections,
                onSave = { y, t, b ->
                    scope.launch {
                        val res = store?.editSection(workspaceId, standupId, currentUserId, y, t, b)
                        if (res is Result.Ok) editOpen = false
                        else if (res is Result.Err) ui?.showToast("Save failed: ${res.error.displayMessage}", ToastKind.Error)
                    }
                },
                onDismiss = { editOpen = false },
            )
        }

        TabContent(activeKey = active.name) {
            when (active) {
                DetailTab.Sections -> {
                    Box(modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.md)) {
                        SectionsViewToggle(
                            active = sectionsView,
                            onSelect = { sectionsView = it },
                        )
                    }
                    detail.templateName?.let {
                        SpanText(
                            text = "Template: $it",
                            modifier = Modifier
                                .margin(bottom = Spacing.md)
                                .fontSize(Typography.caption.size)
                                .color(Palette.textTertiary),
                        )
                    }
                    when (sectionsView) {
                        SectionsView.SlackPreview ->
                            SlackPreviewPanel(
                                detail = detail,
                                currentUserId = currentUserId,
                                onAck = {
                                    scope.launch {
                                        val res = store?.ackStandup(workspaceId, standupId)
                                        if (res is Result.Err) ui?.showToast("Ack failed: ${res.error.displayMessage}", ToastKind.Error)
                                    }
                                },
                                onEdit = { editOpen = true },
                                onSkip = {
                                    scope.launch {
                                        val uid = currentUserId.ifBlank { return@launch }
                                        val res = store?.skipSection(workspaceId, standupId, uid)
                                        if (res is Result.Err) ui?.showToast("Skip failed: ${res.error.displayMessage}", ToastKind.Error)
                                    }
                                },
                                onRevert = {
                                    scope.launch {
                                        val uid = currentUserId.ifBlank { return@launch }
                                        val res = store?.revertSection(workspaceId, standupId, uid)
                                        if (res is Result.Err) ui?.showToast("Revert failed: ${res.error.displayMessage}", ToastKind.Error)
                                    }
                                },
                                modifier = Modifier.fillMaxWidth(),
                            )
                        SectionsView.Sections -> {
                            val shownSections =
                                if (selectedRevision != null && selectedRevision.id != detail.currentRevision?.id) {
                                    selectedRevision.sections
                                } else {
                                    detail.sections
                                }
                            SectionsTab(shownSections, detail.templateSections)
                        }
                    }
                }
                DetailTab.RawActivity -> RawActivityTab(detail, rawActivityRetentionDays)
                DetailTab.AiCost -> AiCostTab(detail)
                DetailTab.Debug -> DebugPanel(detail)
            }
        }
    }
}

@Composable
@Suppress("LongParameterList")
private fun DetailHeader(
    detail: StandupRunDetailDto,
    isAdmin: Boolean,
    rerunInFlight: Boolean,
    onRerun: () -> Unit,
    onRegenerate: (() -> Unit)? = null,
    regenerateInFlight: Boolean = false,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Column(
            modifier = Modifier.styleModifier {
                property("flex", "1 1 auto")
                property("min-width", "0")
            },
        ) {
            SpanText(
                text = detail.standupName,
                modifier = Modifier
                    .fontSize(Typography.h2.size)
                    .fontWeight(Typography.h2.weight)
                    .color(Palette.textPrimary)
                    .styleModifier {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    },
            )
            SpanText(
                text = formatAbsolute(detail.scheduledFor),
                modifier = Modifier
                    .margin(top = Spacing.xxs)
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textSecondary),
            )
        }
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(Spacing.md),
            modifier = Modifier.styleModifier { property("flex-shrink", "0") },
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .backgroundColor(Palette.surfaceSubtle)
                    .borderRadius(Radius.full)
                    .padding(topBottom = 4.px, leftRight = Spacing.xs)
                    .styleModifier {
                        property("gap", "10px")
                        property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                    },
            ) {
                StandupStatusChip(detail.status)
                CostPill(cents = detail.aiCostCents)
                detail.slackMessageUrl?.takeIf { it.isNotBlank() }?.let { url ->
                    Link(
                        path = url,
                        text = "Open in Slack →",
                        modifier = Modifier
                            .padding(right = Spacing.xs)
                            .fontSize(Typography.bodySmall.size)
                            .fontWeight(500)
                            .color(Palette.accent),
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                    )
                }
            }
            if (isAdmin) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
                ) {
                    if (onRegenerate != null) {
                        Button(
                            label = if (regenerateInFlight) "Regenerating…" else "Regenerate",
                            variant = ButtonVariant.Secondary,
                            enabled = !regenerateInFlight,
                            onClick = onRegenerate,
                        )
                    }
                    if (RunStatusRules.canRerun(detail.status)) {
                        Button(
                            label = if (rerunInFlight) "Re-running…" else "Re-run",
                            variant = ButtonVariant.Secondary,
                            enabled = !rerunInFlight,
                            onClick = onRerun,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun RevisionsPillRow(
    revisions: List<StandupRunRevisionDto>,
    selectedId: String?,
    onSelect: (String) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
    ) {
        revisions.sortedBy { it.revisionNumber }.forEach { rev ->
            val isSelected = rev.id == selectedId
            Box(
                modifier = Modifier
                    .backgroundColor(if (isSelected) Palette.accentSubtle else Palette.surfaceSubtle)
                    .color(if (isSelected) Palette.accent else Palette.textSecondary)
                    .padding(topBottom = 4.px, leftRight = Spacing.sm)
                    .borderRadius(Radius.full)
                    .styleModifier {
                        property("cursor", "pointer")
                        property(
                            "border",
                            if (isSelected) "1px solid ${Palette.accent.cssRgb()}" else "1px solid transparent",
                        )
                    }
                    .onClick { onSelect(rev.id) },
            ) {
                SpanText(
                    text = buildRevisionLabel(rev),
                    modifier = Modifier.fontSize(Typography.caption.size),
                )
            }
        }
    }
}

private fun buildRevisionLabel(rev: StandupRunRevisionDto): String {
    val modelShort = rev.aiModel.removePrefix("claude-")
    val currentSuffix = if (rev.isCurrent) " · current" else ""
    return "v${rev.revisionNumber} · $modelShort · ${formatCents(rev.costCents)}$currentSuffix"
}

private enum class SectionsView(val label: String) {
    SlackPreview("Slack preview"),
    Sections("Sections"),
}

private val sectionsViewOptions = SectionsView.entries.map { SegmentedOption(it, it.label) }

@Composable
private fun SectionsViewToggle(
    active: SectionsView,
    onSelect: (SectionsView) -> Unit,
) {
    SegmentedControl(
        options = sectionsViewOptions,
        selected = active,
        onSelect = onSelect,
        ariaLabel = "Run view",
    )
}

@Composable
private fun CostPill(cents: Int) {
    Box(
        modifier = Modifier
            .backgroundColor(Palette.surfaceSubtle)
            .color(Palette.textPrimary)
            .padding(topBottom = 4.px, leftRight = Spacing.sm)
            .borderRadius(Radius.full)
            .styleModifier {
                property("font-family", Typography.FONT_MONO)
                property("text-align", "right")
            },
    ) {
        SpanText(
            text = formatCents(cents),
            modifier = Modifier.fontSize(Typography.bodySmall.size),
        )
    }
}

@Composable
private fun GeneratingBanner(status: StandupRunStatusDto) {
    val headline = if (status == StandupRunStatusDto.GENERATING) "Synthesizing this standup…" else "Queued — waiting to start"
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(Palette.accentSubtle)
            .borderRadius(Radius.md)
            .padding(Spacing.md)
            .styleModifier { property("border", "1px solid ${Palette.accentBorder.cssRgb()}") },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
    ) {
        SpanText(text = headline, modifier = Modifier.fontSize(Typography.body.size).fontWeight(600).color(Palette.textPrimary))
        SpanText(text = "this page refreshes automatically.", modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textSecondary))
    }
}

@Composable
private fun DraftBanner(
    postsAt: Instant,
    isAdmin: Boolean,
    inFlight: Boolean,
    onPostNow: () -> Unit,
    onDiscard: () -> Unit,
) {
    val headline = RunStatusRules.draftHeadline(RunStatusRules.clockLabel(postsAt, TimeZone.currentSystemDefault()))
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(Palette.accentSubtle)
            .borderRadius(Radius.md)
            .padding(Spacing.md)
            .styleModifier { property("border", "1px solid ${Palette.accentBorder.cssRgb()}") },
        verticalArrangement = Arrangement.spacedBy(Spacing.xxs),
    ) {
        SpanText(text = headline, modifier = Modifier.fontSize(Typography.body.size).fontWeight(600).color(Palette.textPrimary))
        SpanText(text = RunStatusRules.DRAFT_EXPLANATION, modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textSecondary))
        if (isAdmin) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(top = Spacing.sm)
                    .styleModifier {
                        property("flex-wrap", "wrap")
                        property("gap", "${Spacing.xs}")
                    },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Button(
                    label = if (inFlight) "Working…" else "Post now",
                    variant = ButtonVariant.Primary,
                    enabled = !inFlight,
                    onClick = onPostNow,
                )
                Button(
                    label = "Discard draft",
                    variant = ButtonVariant.Secondary,
                    enabled = !inFlight,
                    onClick = onDiscard,
                )
            }
        }
    }
}

@Composable
private fun FailureBanner(errorCode: String?, errorMessage: String?, cause: RunCauseDto?) {
    var detailsOpen by remember { mutableStateOf(false) }
    val title = cause?.headline ?: when (errorCode) {
        "linear-unavailable" -> "Linear is unavailable"
        "ai-synthesis-failed" -> "AI synthesis failed"
        "slack-post-failed" -> "Couldn't post to Slack"
        null -> "Standup generation failed"
        else -> "Standup generation failed"
    }
    val body = buildString {
        if (!errorMessage.isNullOrBlank()) append(errorMessage)
        if (!errorCode.isNullOrBlank()) {
            if (isNotEmpty()) append('\n')
            append("code: ").append(errorCode)
        }
        if (isEmpty()) append("No details recorded. Check backend logs for this standup id.")
    }
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(Spacing.md)
            .borderRadius(Radius.md)
            .styleModifier {
                property("background", "rgba(239, 68, 68, 0.06)")
                property("border", "1px solid rgba(239, 68, 68, 0.35)")
            },
    ) {
        SpanText(
            text = title,
            modifier = Modifier
                .fontSize(Typography.body.size)
                .fontWeight(600)
                .color(Palette.error),
        )
        if (cause != null) {
            Row(
                modifier = Modifier.margin(top = Spacing.xs).gap(Spacing.xs),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                CauseAction(cause)
                Button(
                    label = if (detailsOpen) "Hide details" else "Details",
                    variant = ButtonVariant.Ghost,
                    size = ButtonSize.Sm,
                    onClick = { detailsOpen = !detailsOpen },
                )
            }
        }
        if (cause == null || detailsOpen) {
            SpanText(
                text = body,
                modifier = Modifier
                    .margin(top = Spacing.xs)
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textSecondary)
                    .styleModifier {
                        property("white-space", "pre-wrap")
                        property("font-family", Typography.FONT_MONO)
                        property("overflow-wrap", "anywhere")
                    },
            )
        }
    }
}
